import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixTipsSettingsV1TipSettingsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/tip-settings',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/tip-settings',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/tip-settings',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/tip-settings',
        destPath: '',
      },
    ],
    'apps._base_domain_': [
      {
        srcPath: '/_api/tip-settings',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_ecom_tip-settings';

/**
 * Creates a query to retrieve a list of tip settings.
 *
 * The `queryTipSettings()` function builds a query to retrieve a list of tip settings and returns a `TipSettingsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is typically used to run the query using the [find()](https://dev.wix.com/docs/sdk/backend-modules/ecom/tip-settings/tip-settings-query-builder/find) function.
 *
 * You can refine the query by chaining `TipSettingsQueryBuilder` functions onto the query. `TipSettingsQueryBuilder` functions enable you to sort, filter, and control the results that `queryTipSettings()` returns.
 *
 * `queryTipSettings()` runs with the following `TipSettingsQueryBuilder` default that you can override:
 *
 * + `limit` is `50`.
 * + Sorted by `id` in ascending order.
 *
 * The functions that are chained to `queryTipSettings()` are applied in the order they are called. For example, if you apply `ascending("tipType")` and then `ascending("locationId")`, the results are sorted first by the `"tipType"`, and then, if there are multiple results with the same `"tipType"`, the items are sorted by `"locationId"`.
 *
 * The following `TipSettingsQueryBuilder` functions are supported for the `queryTipSettings()` function. For a full description of the tip settings object, see the object returned for the [items](https://dev.wix.com/docs/sdk/backend-modules/ecom/tip-settings/tip-settings-query-result/items) property in `TipSettingsQueryResult`.
 */
export function queryTipSettings(payload: object): RequestOptionsFactory<any> {
  function __queryTipSettings({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.tips.settings.v1.tip_settings',
      method: 'POST' as any,
      methodFqn: 'wix.tips.settings.v1.TipSettingsService.QueryTipSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixTipsSettingsV1TipSettingsServiceUrl({
        protoPath: '/v1/tip-settings/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'tipSettings.createdDate' },
              { path: 'tipSettings.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [{ path: 'tipSettings.presets.value' }],
          },
        ]),
    };

    return metadata;
  }

  return __queryTipSettings;
}

/**
 * Updates a `tipSettings` object.
 *
 *
 * You can't update the `default` field.
 */
export function updateTipSettings(payload: object): RequestOptionsFactory<any> {
  function __updateTipSettings({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'tipSettings.createdDate' },
          { path: 'tipSettings.updatedDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [{ path: 'tipSettings.presets.value' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.tips.settings.v1.tip_settings',
      method: 'PATCH' as any,
      methodFqn: 'wix.tips.settings.v1.TipSettingsService.UpdateTipSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixTipsSettingsV1TipSettingsServiceUrl({
        protoPath: '/v1/tip-settings',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'tipSettings.createdDate' },
              { path: 'tipSettings.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [{ path: 'tipSettings.presets.value' }],
          },
        ]),
    };

    return metadata;
  }

  return __updateTipSettings;
}

/**
 * Creates a `tipSettings` object.
 *
 *
 * You can't create a `tipSettings` object with `{"default": true}`.
 */
export function createTipSettings(payload: object): RequestOptionsFactory<any> {
  function __createTipSettings({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'tipSettings.createdDate' },
          { path: 'tipSettings.updatedDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [{ path: 'tipSettings.presets.value' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.tips.settings.v1.tip_settings',
      method: 'POST' as any,
      methodFqn: 'wix.tips.settings.v1.TipSettingsService.CreateTipSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixTipsSettingsV1TipSettingsServiceUrl({
        protoPath: '/v1/tip-settings',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'tipSettings.createdDate' },
              { path: 'tipSettings.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [{ path: 'tipSettings.presets.value' }],
          },
        ]),
    };

    return metadata;
  }

  return __createTipSettings;
}

/**
 * Creates a default `tipSettings` object.
 *
 *
 * The call fails if there is already an existing default `tipSettings` object.
 */
export function createDefaultTipSettings(
  payload: object
): RequestOptionsFactory<any> {
  function __createDefaultTipSettings({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'tipSettings.createdDate' },
          { path: 'tipSettings.updatedDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [{ path: 'tipSettings.presets.value' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.tips.settings.v1.tip_settings',
      method: 'POST' as any,
      methodFqn:
        'wix.tips.settings.v1.TipSettingsService.CreateDefaultTipSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixTipsSettingsV1TipSettingsServiceUrl({
        protoPath: '/v1/tip-settings/create-default',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'tipSettings.createdDate' },
              { path: 'tipSettings.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [{ path: 'tipSettings.presets.value' }],
          },
        ]),
    };

    return metadata;
  }

  return __createDefaultTipSettings;
}

/**
 * Deletes a `tipSettings` object.
 *
 *
 * You can't delete the default `tipSettings` object.
 */
export function deleteTipSettings(payload: object): RequestOptionsFactory<any> {
  function __deleteTipSettings({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.tips.settings.v1.tip_settings',
      method: 'DELETE' as any,
      methodFqn: 'wix.tips.settings.v1.TipSettingsService.DeleteTipSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixTipsSettingsV1TipSettingsServiceUrl({
        protoPath: '/v1/tip-settings/{tipSettingsId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteTipSettings;
}
